import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import { Box, Button, CircularProgress, Dialog, IconButton, Slide, Typography } from "@mui/material";
import React, { useState } from "react";
import { usePagesTranslation } from "../../../../pages/locales";
import { getIapResultUserMessage } from "../_stubs/iapFeedbackMessages";
import {
    formatHintPointsPackLabel,
    HINT_POINT_COST,
    HINT_POINTS_PACK_AMOUNT,
    shouldUseSuggestedHintPointsPriceFallback,
} from "../lib/hintPointsConstants";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { settingsPrimaryButtonSx } from "../styles/premiumCtaStyles";

const systemFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif';

function Transition(props) {
    return <Slide direction="up" {...props} />;
}

/**
 * Buy assistant coins pack @ store price — separate from puzzle-unlock credits.
 */
export default function HintPointsSheet({
    open,
    onClose,
    pointBalance = 0,
    packAmount = HINT_POINTS_PACK_AMOUNT,
    packPrice = null,
    packagesLoading = false,
    isProcessing = false,
    isIapAvailable = true,
    onPurchase = null,
}) {
    const { t } = usePagesTranslation();
    const { tokens } = useSlidingAppearance();
    const [inlineError, setInlineError] = useState("");
    const allowCadFallback = shouldUseSuggestedHintPointsPriceFallback();
    const priceLabel = formatHintPointsPackLabel(packPrice, { allowCadFallback });
    const busy = isProcessing || packagesLoading;

    const handleDismiss = () => {
        if (isProcessing) return;
        setInlineError("");
        onClose?.();
    };

    const handlePurchase = async () => {
        setInlineError("");
        if (!onPurchase) return;
        const result = await onPurchase();
        if (result?.cancelled) return;
        if (result?.success) {
            onClose?.();
            return;
        }
        setInlineError(
            getIapResultUserMessage(result?.error, t) ||
                t("native.hintPoints.purchaseFailed", {
                    defaultValue: "Could not complete purchase. Try again.",
                }),
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleDismiss}
            TransitionComponent={Transition}
            fullWidth
            PaperProps={{
                sx: {
                    mx: 1.5,
                    borderRadius: "16px",
                    bgcolor: tokens.elevatedCardBg,
                    border: tokens.elevatedCardBorder,
                    maxWidth: 420,
                },
            }}
        >
            <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <TipsAndUpdatesRoundedIcon sx={{ color: "#2EC4B6", fontSize: 28 }} />
                        <Typography
                            component="h2"
                            sx={{
                                fontFamily: systemFont,
                                fontWeight: 700,
                                fontSize: "1.125rem",
                                color: tokens.text,
                            }}
                        >
                            {t("native.hintPoints.sheetTitle", { defaultValue: "Assistant coins" })}
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label={t("common.close", { defaultValue: "Close" })}
                        onClick={handleDismiss}
                        disabled={isProcessing}
                        sx={{ minWidth: 44, minHeight: 44 }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </Box>

                <Typography
                    sx={{
                        fontFamily: systemFont,
                        fontSize: "0.9375rem",
                        fontWeight: 600,
                        color: "#2EC4B6",
                        mb: 1,
                    }}
                >
                    {t("native.hintPoints.balanceNow", {
                        defaultValue: "You have {{count}} coins now",
                        count: pointBalance ?? 0,
                    })}
                </Typography>

                <Typography
                    sx={{
                        fontFamily: systemFont,
                        fontSize: "0.9375rem",
                        color: tokens.textMuted,
                        mb: 2,
                    }}
                >
                    {t("native.hintPoints.sheetBody", {
                        defaultValue: "Ghost guide assists cost {{cost}} coins each.",
                        cost: HINT_POINT_COST,
                    })}
                </Typography>

                <Button
                    type="button"
                    fullWidth
                    variant="contained"
                    disabled={busy || !isIapAvailable || !onPurchase}
                    onClick={() => {
                        void handlePurchase();
                    }}
                    sx={settingsPrimaryButtonSx({
                        animate: !busy && isIapAvailable && Boolean(onPurchase),
                    })}
                >
                    {busy ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                        <Box component="span">
                            {t("native.hintPoints.buyPack", {
                                defaultValue: "Buy {{amount}} assistant coins — {{price}}",
                                amount: packAmount,
                                price:
                                    packPrice ||
                                    (allowCadFallback
                                        ? "$1.99"
                                        : t("native.settings.loadingPrice", {
                                              defaultValue: "Loading price…",
                                          })),
                            })}
                        </Box>
                    )}
                </Button>

                {inlineError ? (
                    <Typography
                        role="alert"
                        sx={{
                            mt: 1.5,
                            fontFamily: systemFont,
                            fontSize: "0.8125rem",
                            color: "error.main",
                        }}
                    >
                        {inlineError}
                    </Typography>
                ) : null}
            </Box>
        </Dialog>
    );
}
